import Vue from "vue";
import axios from "@/plugins/axios";
import utils from "@/stores/utils";
import { resolvePermissionCheck } from "@/utils/groupPermissions";
import Decimal from "decimal.js";

const state = {
    customers: [],
    customers_loading: false,
    customers_loading_errors: null,

    deliverysites: [],
    deliverysites_loading: false,
    deliverysites_loading_errors: null,

    providerorders: [],
    providerorders_loading: false,
    providerorders_loading_errors: null,

    providerorderitem: {},
    providerorderitem_saving: false,
    providerorderitem_saving_errors: null,

    provider: null,
    product: {},
    products_filters: {},
};

function providerSellerFilterValue(provider) {
    if (provider == null) {
        return null;
    }
    return typeof provider === "object" ? provider.id : provider;
}

function permissionDeniedError() {
    const message = Vue.prototype.$translate
        ? Vue.prototype.$translate("errors.permission_denied")
        : "Permission denied";
    return {
        success: false,
        code: 403,
        message,
        details: { non_field_errors: [message] },
    };
}

function serializeProviderOrderItemPayload(providerorderitem) {
    const payload = JSON.parse(JSON.stringify(providerorderitem));
    const decimalFields = [
        "unit_amount",
        "unit_discount",
        "force_vat_rate",
        "unit_agent_commission",
        "unit_amount_excl_vat",
        "unit_amount_incl_vat",
        "unit_discount_excl_vat",
        "unit_discount_incl_vat",
        "unit_vat_amount",
        "total_discount_excl_vat",
        "total_discount_incl_vat",
        "total_amount_excl_vat",
        "total_amount_incl_vat",
        "unit_margin_excl_vat",
        "total_margin_excl_vat",
    ];
    for (const field of decimalFields) {
        const value = providerorderitem[field];
        if (value instanceof Decimal) {
            payload[field] = value.toNumber();
        }
    }
    delete payload.catalogentry_data;
    delete payload.order_data;
    return payload;
}

const mutations = {
    updateProvider(state, provider) {
        state.provider = provider;
        state.providerorders_filters = {
            exclude_readonly: true,
            ready: false,
            ordering: "id",
            limit: 1000,
        };
    },

    updateCustomers(state, customers) {
        state.customers = customers;
    },
    updateCustomersLoading(state, loading) {
        state.customers_loading = loading;
    },
    updateCustomersLoadingErrors(state, errors) {
        state.customers_loading_errors = errors;
    },

    updateDeliverySites(state, deliverysites) {
        state.deliverysites = deliverysites;
    },
    updateDeliverySitesLoading(state, loading) {
        state.deliverysites_loading = loading;
    },
    updateDeliverySitesLoadingErrors(state, errors) {
        state.deliverysites_loading_errors = errors;
    },

    updateProviderOrders(state, providerorders) {
        state.providerorders = providerorders;
    },
    updateProviderOrdersLoading(state, loading) {
        state.providerorders_loading = loading;
    },
    updateProviderOrdersLoadingErrors(state, errors) {
        state.providerorders_loading_errors = errors;
    },

    updateProviderOrderItemSaving(state, saving) {
        state.providerorderitem_saving = saving;
    },
    updateProviderOrderItemSavingErrors(state, errors) {
        state.providerorderitem_saving_errors = errors;
    },

    updateProviderOrderItem(state, providerorderitem) {
        state.providerorderitem = providerorderitem;
        state.providerorders_filters = {
            exclude_readonly: true,
            ready: false,
            ordering: "id",
            limit: 1000,
            seller: providerSellerFilterValue(state.provider),
        };

        state.products_filters = {
            product: state.providerorderitem.product,
        };
        state.providerorderitem_saving_errors = null;
    },

    updateStockProduct(state, product) {
        state.product = product;
    },
};
const actions = {
    async fetchDeliverySites({ commit, rootGetters, state }, params) {
        if (
            rootGetters["session/current_user_permissions"].indexOf(
                "core.view_deliverysite"
            ) === -1
        ) {
            return;
        }

        commit("updateDeliverySitesLoading", true);
        commit("updateDeliverySitesLoadingErrors", null);

        const customer_id = params.customer_id;

        try {
            const response = await axios.get(
                `/api/deliverysites/`,
                { params: { limit: 1000 } }
            );
            commit("updateDeliverySites", response.data.results);
            return response;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit("updateDeliverySitesLoadingErrors", error.details);
            throw error;
        } finally {
            commit("updateDeliverySitesLoading", false);
        }
    },

    async fetchProviderOrders({ commit, rootGetters, state }, params) {
        if (
            rootGetters["session/current_user_permissions"].indexOf(
                "order.view_order"
            ) === -1
        ) {
            return;
        }

        commit("updateProviderOrdersLoading", true);
        commit("updateProviderOrdersLoadingErrors", null);

        try {
            const response = await axios.get("/api/providerorders/", {
                params: state.providerorders_filters
            });
            commit("updateProviderOrders", response.data.results);
            if (response.data.results.length > 0 && !state.providerorderitem.id) {
                commit("updateProviderOrderItem", {
                    ...state.providerorderitem,
                    order: response.data.results[0].id,
                    order_data: response.data.results[0],
                });
            }
            return response;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit("updateProviderOrdersLoadingErrors", error.details);
            throw error;
        } finally {
            commit("updateProviderOrdersLoading", false);
        }
    },

    async saveProviderOrderItem({ commit, rootGetters, state }, params) {
        const permissions = rootGetters["session/current_user_permissions"];
        const hasAddPermission = resolvePermissionCheck(
            permissions,
            "order.add_orderitem"
        );
        const hasChangePermission = resolvePermissionCheck(
            permissions,
            "order.change_orderitem"
        );

        if (!state.providerorderitem.id && !hasAddPermission) {
            const error = permissionDeniedError();
            commit("updateProviderOrderItemSavingErrors", error.details);
            throw error;
        }
        if (state.providerorderitem.id && !hasChangePermission) {
            const error = permissionDeniedError();
            commit("updateProviderOrderItemSavingErrors", error.details);
            throw error;
        }

        commit("updateProviderOrderItemSaving", true);
        commit("updateProviderOrderItemSavingErrors", {});

        let url = "/api/providerorderitems/";
        let method = axios.post;

        if (state.providerorderitem.id) {
            url = `/api/providerorderitems/${state.providerorderitem.id}/`;
            method = axios.put;
        }
        const payload = serializeProviderOrderItemPayload(state.providerorderitem);
        try {
            return await method(url, payload);
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit("updateProviderOrderItemSavingErrors", error.details);
            throw error;
        } finally {
            commit("updateProviderOrderItemSaving", false);
        }
    },

    async deleteProviderOrderItem({ commit, rootGetters, state }, params) {
        if (
            rootGetters["session/current_user_permissions"].indexOf(
                "order.delete_orderitem"
            ) === -1
        ) {
            return;
        }

        try {
            return await axios.delete(
                `/api/providerorderitems/${state.providerorderitem.id}/`
            );
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            throw error;
        }
    },

    async init({ commit, state, dispatch }, params) {

        const providerorderitem = JSON.parse(JSON.stringify(
            {
                count: params.product?.packaging,
                ...(params.providerorderitem || {})

            }));
        if (params.product?.stock_data) {
            providerorderitem.count = Math.min(
                providerorderitem.count,
                params.product?.stock_data?.count
            );
        }
        const catalogentry = providerorderitem.catalogentry_data;
        if (
            !providerorderitem.id &&
            (providerorderitem.unit_amount == null || providerorderitem.unit_amount === "") &&
            catalogentry?.unit_amount != null && catalogentry.unit_amount !== ""
        ) {
            providerorderitem.unit_amount = new Decimal(catalogentry.unit_amount).toFixed();
            providerorderitem.unit_amount_includes_vat =
                catalogentry.unit_amount_includes_vat ??
                catalogentry.catalog_data?.prices_include_vat ?? false;
        }
        const provider =
            params.provider && typeof params.provider === "object"
                ? params.provider
                : params.provider != null
                  ? { id: params.provider }
                  : null;
        commit('updateProvider', provider);
        commit('updateProviderOrderItem', providerorderitem);
        if (!providerorderitem.order) {
            dispatch('fetchProviderOrders');
        }
    }
};

export default {
    namespaced: true,
    state,
    actions,
    mutations,
};
